import { useState } from "react";
import { useDispatch } from "react-redux";
import api from "@/services/api";
import type { OtpResponse } from "../types/otp";
import { otpService } from "@/services/otp.service";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { setCredentials } from "../store/authSlice";
import { RegisterUserPayload } from "@/types/register";
import { extractErrorMessage } from "@/utils/error-utils";

export const useRegister = (
  prefillEmail?: string | null,
  inviteMode?: boolean,
  inviteToken?: string | null,
  showVerificationToast: boolean = true
) => {
  const [verifyType, setVerifyType] = useState<"email" | "mobile" | "sms">(
    "email"
  );
  const [inputValue, setInputValue] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const dispatch = useDispatch();

  // Send OTP for registration
  // Accepts: { dialCode, mobile }
  // Accepts: { dialCode, mobile }
  const sendOtp = async ({
    dialCode,
    mobile,
    email,
  }: {
    dialCode?: string;
    mobile?: string;
    email?: string;
  }): Promise<OtpResponse> => {
    setError("");
    setLoading(true);
    try {
      let payload: any;
      if (
        (verifyType === "sms" || verifyType === "mobile") &&
        mobile &&
        dialCode
      ) {
        payload = {
          send_to: mobile,
          dialCode,
          mode: "sms",
          type: inviteMode || inviteToken ? "member_verify" : "member_register",
        };
      } else if (email) {
        payload = {
          send_to: inviteMode || inviteToken ? prefillEmail : email,
          mode: "email",
          type: inviteMode || inviteToken ? "member_verify" : "member_register",
        };
      } else {
        payload = {
          send_to: inviteMode ? prefillEmail : mobile,
          mode: "sms",
          type: inviteMode || inviteToken ? "member_verify" : "member_register",
        };
      }
      const response = await otpService.sendOtp(payload);
      setOtpSent(!!response.success);
      // if (response.success) {
      //   showToast({ message: "OTP sent", variant: "success" });
      // }
      return response;
    } catch (err: any) {
      const msg = extractErrorMessage(err, "Failed to send OTP. Try again.");
      setError(msg);
      showToast({ message: msg, variant: "error" });
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP only (no registration)
  // Accepts: otpArg, { dialCode, mobile }
  // Accepts: otpArg, { dialCode, mobile }
  const verifyOtp = async (
    otpArg?: string,
    opts?: { dialCode?: string; mobile?: string; email?: string }
  ) => {
    const otpToUse = otpArg ?? otp;
    if (otpToUse.length !== 6) return;
    setError("");
    setLoading(true);
    try {
      let payload: any;
      if (
        (verifyType === "sms" || verifyType === "mobile") &&
        opts?.mobile &&
        opts?.dialCode
      ) {
        payload = {
          otp: otpToUse,
          mode: "sms",
          type: "member_register",
          send_to: opts.mobile,
          dialCode: opts.dialCode,
        };
      } else if (opts?.email) {
        payload = {
          otp: otpToUse,
          mode: "email",
          type: "member_register",
          send_to: opts.email,
        };
      } else {
        payload = {
          otp: otpToUse,
          mode: "email",
          type: "member_register",
          send_to: inviteMode ? prefillEmail : inputValue,
        };
      }
      const response = await otpService.verifyOtp(payload);
      const message =
        payload.mode === "email" ? "Email verified" : "Mobile number verified";
      if (response.success) {
        if (showVerificationToast) {
          showToast({
            message: message,
            variant: "success",
          });
        }
      } else {
        setError(response.message || "OTP verification failed");
        showToast({
          message: response.message || "OTP verification failed",
          variant: "error",
        });
      }
      return response;
    } catch (err: any) {
      const msg = extractErrorMessage(err, "OTP verification failed");
      setError(msg);
      showToast({
        message: msg,
        variant: "error",
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Register user (after OTP) - legacy
  const registerUser = async (payload: RegisterUserPayload, token?: string) => {
    setError("");
    setLoading(true);
    try {
      let response;
      // If token argument is present, use invite/consent endpoint
      if (token) {
        response = await api.post("/register/member", { ...payload, token });
      } else {
        response = await api.post("/register", payload);
      }
      if (response.data.token && response.data.member) {
        // Update localStorage
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("member", JSON.stringify(response.data.member));
        sessionStorage.removeItem("token");

        // Update Redux state
        dispatch(
          setCredentials({
            token: response.data.token,
            member: response.data.member,
          })
        );

        return { success: true };
      }
      return response.data;
    } catch (err: any) {
      const msg = extractErrorMessage(err, "Registration failed");
      setError(msg);
      showToast({ message: msg, variant: "error" });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // New: Register with name and all fields, for use in SignupConfirm
  const registerWithName = async ({
    name,
    mobile,
    dialCode,
    email,
    onSuccess,
    onError,
  }: {
    name: string;
    mobile?: string;
    dialCode?: string;
    email?: string;
    onSuccess?: (data: any) => void;
    onError?: (err: any) => void;
  }) => {
    setError(null);
    setLoading(true);
    try {
      // Validate inputs before making API call
      if (!name || name.trim() === "") {
        const errorMsg = "Name is required";
        setError(errorMsg);
        showToast({ message: errorMsg, variant: "error" });
        if (onError) onError({ message: errorMsg });
        return { success: false, message: errorMsg };
      }

      // Validate contact method - either email or mobile+dialCode should be provided
      if (!email && (!mobile || !dialCode)) {
        const errorMsg =
          "Either email or mobile number with country code is required";
        setError(errorMsg);
        showToast({ message: errorMsg, variant: "error" });
        if (onError) onError({ message: errorMsg });
        return { success: false, message: errorMsg };
      }

      const payload: any = { first_name: name };
      if (mobile) payload.mobile = mobile;
      if (dialCode) payload.dialCode = dialCode;
      if (email) payload.email = email;

      const response = await registerUser(payload);

      if (response?.success) {
        showToast({ message: "Registration successful!", variant: "success" });
        if (onSuccess) onSuccess(response);
      } else {
        const errorMsg = response?.message || "Registration failed";
        setError(errorMsg);
        showToast({ message: errorMsg, variant: "error" });
        if (onError) onError(response);
      }
      return response;
    } catch (err: any) {
      const errorMsg = extractErrorMessage(err, "Registration failed");
      setError(errorMsg);
      showToast({ message: errorMsg, variant: "error" });
      if (onError) onError(err);
      return { success: false, message: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  const deleteMemberViaRegister = async (memberId: string) => {
    return api.delete("/register/member", { data: { memberId } });
  };

  const createOwnFamily = async (payload: any, token?: string) => {
    if (!payload) {
      setError("Payload is required");
      setLoading(false);
      return;
    }

    if (!token) {
      setError("Token is required");
      setLoading(false);
      return;
    }

    setError("");
    setLoading(true);
    try {
      // If token argument is present, use invite/consent endpoint
      const response = await api.post("/register/member/createOwn", {
        ...payload,
        token,
      });
      if (response.data.token && response.data.member) {
        // Update localStorage
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("member", JSON.stringify(response.data.member));
        sessionStorage.removeItem("token");

        // Update Redux state
        dispatch(
          setCredentials({
            token: response.data.token,
            member: response.data.member,
          })
        );

        return { success: true };
      }
      return response.data;
    } catch (err: any) {
      const msg = extractErrorMessage(err, "Registration failed");
      setError(msg);
      showToast({ message: msg, variant: "error" });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    verifyType,
    setVerifyType,
    inputValue,
    setInputValue,
    countryCode,
    setCountryCode,
    otpSent,
    setOtpSent,
    otp,
    setOtp,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    error,
    setError,
    loading,
    sendOtp,
    verifyOtp,
    registerUser,
    createOwnFamily,
    registerWithName,
    deleteMemberViaRegister,
  };
};
